<template>
  <!-- WIP: TableViewManager can accept nested fields -->
  <div>
    <McButton
      @click="showModal = !showModal"
      variant="secondary"
      :text="$t('global.viewSetting')"
      icon="icon-outline_layout"
    />

    <b-modal v-model="showModal" size="md" modal-class="modal-manage-view">
      <div class="wrapper p-2">
        <div class="section section-information">
          <p>{{ $t('global.object.remarks') }}</p>
          <div class="section-information__content p-2">
            <p>{{ $t('global.click') }} <McCheckbox v-model="alwaysChecked" /> {{ $t('global.text.toShowColumn') }}</p>
            <p>{{ $t('global.click') }} <i class="icon-outline_move"></i> {{ $t('global.text.toMoveColumn') }}</p>
          </div>
        </div>
        <div class="section section-column-manager">
          <p>{{ $t('global.text.columnAdjustment') }}</p>
          <div class="section-column-manager__content">
            <div class="section-column-manager__header d-flex justify-content-between p-3">
              <div class="d-flex gap-2">
                <McCheckbox @change="handleChangeCheckAll" :model-value="showAllColumn" />
                <span>{{ $t('global.text.showAllColumn') }}</span>
              </div>
              <button @click="handleResetColumn">{{ $t('global.resetAsset') }}</button>
            </div>
            <draggable v-model="fieldsTemp" class="section-column-manager__draggable mb-3">
              <transition-group tag="ul">
                <li
                  class="section-column-manager__draggable-item"
                  v-for="field in fieldsTemp"
                  :key="field.key"
                >
                  <div class="d-flex align-items-center gap-2">
                    <i class="icon-outline_move"></i>
                    <div class="__divider"></div>
                    <span class="d-flex gap-2 w-100"
                      ><McCheckbox v-model="field.shown" :disabled="field.isLocked" />
                      {{ field.label }}</span
                    >
                    <i v-if="field.fields" class="toggle-list icon-outline_chevron-down"></i>
                  </div>

                  <template v-if="field.fields">
                    <hr />

                    <draggable v-model="field.fields">
                      <transition-group tag="ul">
                        <li
                          v-for="item in field.fields"
                          :key="item.key"
                          class="section-column-manager__draggable-sub-item"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <i class="icon-outline_move"></i>
                            <div class="__divider"></div>
                            <span class="d-flex gap-2 w-100"
                              ><McCheckbox v-model="item.shown" :disabled="item.isLocked" />
                              {{ item.label }}</span
                            >
                          </div>
                        </li>
                      </transition-group>
                    </draggable>
                  </template>
                </li>
              </transition-group>
            </draggable>
          </div>
        </div>
      </div>
      <template #modal-header>
        <span class="__title">{{ $t('global.viewSetting') }}</span>
      </template>
      <template #modal-footer="{ cancel }">
        <b-row class="modal__footer mx-0">
          <b-col sm="12" md="12" class="modal__footer-btn">
            <McButton variant="secondary" class="me-2" :text="$t('global.action.cancel')" @click="cancel" />
            <McButton variant="primary" :text="$t('global.action.apply')" @click="handleAccept" />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'

export default {
  components: {
    McButton,
    McCheckbox,
    draggable,
  },
  props: {
    defaultColumnOrder: {
      type: Array,
      required: true,
    },
    currentColumnOrder: {
      type: Array,
      required: true,
    },
  },
  emits: ['setColumn'],
  data() {
    return {
      showModal: false,
      fieldsTemp: [],
      alwaysChecked: true,
    }
  },
  computed: {
    showAllColumn() {
      return this.fieldsTemp.every((field) => field.shown)
    },
  },
  watch: {
    showModal(newValue) {
      if (!newValue) return
      this.fieldsTemp = cloneDeep(this.currentColumnOrder)
    },
    async alwaysChecked() {
      await this.$nextTick()
      this.alwaysChecked = true
    },
  },

  methods: {
    handleAccept() {
      this.$emit('setColumn', cloneDeep(this.fieldsTemp))
      this.showModal = false
    },
    handleResetColumn() {
      this.fieldsTemp = cloneDeep(this.defaultColumnOrder)
    },
    handleChangeCheckAll(value) {
      this.fieldsTemp = this.fieldsTemp.map((field) => ({
        ...field,
        shown: field.isLocked ? true : value,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.modal-manage-view) {
  .modal-dialog {
    border-radius: 0px;
    max-height: 100vh;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;

    .modal__footer-btn {
      display: flex;
    }
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }

  .modal-body {
    overflow: hidden;
  }

  .wrapper {
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.section {
  :deep(input[type='checkbox']) {
    margin-bottom: 2px;
  }

  & > p {
    color: $teal-300;
    font-weight: 600;
  }

  &-information {
    &__content {
      border: 2px solid $blue-100;
      border-radius: 4px;
      margin-bottom: 20px;

      & > p {
        display: flex;
        gap: 5px;
        align-items: center;
        margin-bottom: 0;

        &:first-child {
          margin-bottom: 4px;
        }
      }
    }
  }

  &-column-manager {
    overflow: hidden;
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      height: 100%;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }

    &__draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      ul {
        padding: 0;
      }

      &-item,
      &-sub-item {
        list-style-type: none;
        cursor: move;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .__divider {
          background-color: $teal-100;
          height: 16px;
          width: 1px;
        }

        .toggle-list {
          font-size: 20px;

          &::before {
            color: #3d69a7;
          }
        }
      }

      &-sub-item {
        list-style-type: none;
        border: 1px solid #cfdae9;
      }
    }
  }
}

.icon-outline_move {
  color: $blue-400;
  font-size: 16px;
}
</style>
